import React, {useCallback, useEffect, useRef, useState} from 'react'

import {OmicsContextPopover} from './OmicsContextPopover'
import type {ContextUsage} from './omicsContextUsage'
import {contextUsageSeverity, formatContextWindowTitle} from './omicsContextUsage'

const RING_SIZE = 32
const STROKE = 3
const R = (RING_SIZE - STROKE) / 2
const C = RING_SIZE / 2
const CIRCUMFERENCE = 2 * Math.PI * R

export interface OmicsContextRingProps {
  usage: ContextUsage
  children: React.ReactNode
}

export const OmicsContextRing: React.FC<OmicsContextRingProps> = ({usage, children}) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const severity = contextUsageSeverity(usage.percent)
  const offset = CIRCUMFERENCE * (1 - Math.min(100, usage.percent) / 100)
  const contextTitle = formatContextWindowTitle(usage.percent)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div className='omics-context-ring' ref={wrapRef} title={contextTitle}>
      <div className='omics-context-ring__frame'>
        <svg
          className='omics-context-ring__svg'
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          aria-hidden
        >
          <circle
            className='omics-context-ring__track'
            cx={C}
            cy={C}
            r={R}
            fill='none'
            strokeWidth={STROKE}
          />
          <circle
            className={`omics-context-ring__fill omics-context-ring__fill--${severity}`}
            cx={C}
            cy={C}
            r={R}
            fill='none'
            strokeWidth={STROKE}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            strokeLinecap='round'
            transform={`rotate(-90 ${C} ${C})`}
          />
        </svg>
        <button
          type='button'
          className={`omics-context-ring__trigger omics-context-ring__trigger--${severity}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={`Context window ${usage.percent} percent full`}
          aria-expanded={open}
        />
        <span className='omics-context-ring__children'>{children}</span>
      </div>
      {open ? <OmicsContextPopover usage={usage} onClose={close} /> : null}
    </div>
  )
}
